'use client';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { MediaType } from '@repo/ui/src/types/image';
import { ToutContentProps } from '@repo/ui/src/types/toutContent';
import { useRef, useEffect, useState } from 'react';
import { ToutContent } from '@repo/ui/src/components/ToutContent/_toutContent';
import { classNames } from '@repo/ui/src/utils/classNames';
import { appConfig } from '../../utils/appConfig';
import styles from './_GridCard.module.css';
import BackgroundVideo from '@repo/ui/src/components/BackgroundVideo';

interface CardProps {
  img: {
    image: MediaType;
    altTag: string;
  };
  toutContent: ToutContentProps;
  largeCard: boolean;
  reverse?: boolean;
  isMeaterMobile?: boolean;
  video?: MediaType;
}

const GridCard = ({ img, toutContent, largeCard, reverse, isMeaterMobile, video }: CardProps) => {
  const ref = useRef<any>(null);
  const [translateHeight, setHeight] = useState(0);
  const imageUrl = buildImageSrc(img.image, 700, 1, 1);

  useEffect(() => {
    const element = ref.current;
    if (element) {
      const bottomContent = element.querySelector('.bottom-content');
      const bottomContentHeight = bottomContent?.clientHeight;
      if (bottomContentHeight) {
        setHeight(bottomContentHeight);
      }
    }
  }, []);

  if (isMeaterMobile) {
    return (
      <li className="relative w-full aspect-square overflow-hidden" tabIndex={0}>
        {video ? (
          <BackgroundVideo
            video={video}
            width={700}
            fillContainer
            className="absolute inset-0 h-full"
            playButtonPosition="right"
          />
        ) : (
          <img src={imageUrl} alt="" className="w-full h-full object-cover" />
        )}
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-[rgba(16,16,16,1)] to-[rgba(0,0,0,0)]" />
        <div className="absolute bottom-0 left-0 w-full p-5">
          <ToutContent
            dark
            headingStyleVariant="h5"
            heading={toutContent.heading}
            {...toutContent}
            largeCard={largeCard}
            align="left"
            divider
            buttonSize="small"
          />
        </div>
      </li>
    );
  }

  return (
    <li
      className={classNames(
        'group/card accCard',
        styles.gridCard,
        reverse ? styles.reverse : styles.normal,
      )}
      ref={ref}
      tabIndex={0}
      style={{
        ['--translate-height' as any]: `${translateHeight + 4}px`,
      }}
    >
      {video ? (
        <div className="row-start-1 col-start-1 w-full aspect-square">
          <BackgroundVideo
            video={video}
            width={700}
            fillContainer
            className="h-full"
            playButtonPosition="right"
          />
        </div>
      ) : (
        <img
          src={imageUrl}
          alt=""
          className="w-full h-auto object-cover row-start-1 col-start-1 aspect-square"
        />
      )}
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-[rgba(16,16,16,1)] to-[rgba(0,0,0,0)]" />
      <div
        className="tout flex px-5 flex-col justify-end row-start-1 col-start-1 w-full h-full transition-transform duration-[600ms]
          [&_.bottom-content]:absolute [&_.bottom-content]:pr-5 [&_.bottom-content]:transition-all [&_.bottom-content]:duration-[800ms] [&_.bottom-content]:ease-out
          group-hover/card:[&_.bottom-content]:opacity-100 focus:[&_.bottom-content]:opacity-100"
      >
        <ToutContent
          dark
          headingStyleVariant={appConfig.isTraeger ? 'h5' : 'h3'}
          heading={toutContent.heading}
          {...toutContent}
          largeCard={largeCard}
          align="left"
          divider
          buttonSize="small"
          className={appConfig.isMeater && '[&_h2]:uppercase'}
        />
      </div>
    </li>
  );
};

export default GridCard;
